import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import SecureLS from "secure-ls";
import { Outlet, useNavigate } from "react-router-dom";
import InventoryReminderDialog from "../components/InventoryReminderDialog";
import { fetchDailyInventoryStatus } from "../services/dailyInventoryApi";

const ls = new SecureLS({ encodingType: "aes" });
const InventoryModuleStatusContext = createContext(null);

const getReminderMode = (status) => {
  if (!status) return null;
  if (status.beginningInventory?.status !== "completed") return "beginning";
  if (
    status.zReadingCompleted &&
    status.endingInventory?.status !== "completed"
  ) {
    return "ending";
  }
  return null;
};

const getDailyTabStatus = (status) => {
  if (!status) return "Opening and closing counts";
  if (status.beginningInventory?.status !== "completed") {
    return "Beginning not yet accomplished";
  }
  if (
    status.zReadingCompleted &&
    status.endingInventory?.status !== "completed"
  ) {
    return "Ending not yet accomplished";
  }
  return status.endingInventory?.status === "completed"
    ? "Today's counts accomplished"
    : "Beginning accomplished";
};

export const InventoryModuleStatusProvider = () => {
  const navigate = useNavigate();
  const [todayStatus, setTodayStatus] = useState(null);
  const [yesterdayStatus, setYesterdayStatus] = useState(null);
  const [reminderMode, setReminderMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayDate, setTodayDate] = useState(() =>
    dayjs().format("YYYY-MM-DD")
  );
  const stockAlertShownRef = useRef(false);
  const yesterdayDate = dayjs(todayDate)
    .subtract(1, "day")
    .format("YYYY-MM-DD");
  const token = ls.get("authToken");

  const refreshStatuses = useCallback(
    async ({ showReminder = false } = {}) => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const [today, yesterday] = await Promise.all([
          fetchDailyInventoryStatus({
            token,
            businessDate: todayDate,
          }),
          fetchDailyInventoryStatus({
            token,
            businessDate: yesterdayDate,
          }),
        ]);
        setTodayStatus(today);
        setYesterdayStatus(yesterday);
        if (showReminder) setReminderMode(getReminderMode(today));
      } catch (error) {
        console.error("Unable to load inventory reminders:", error);
      } finally {
        setLoading(false);
      }
    },
    [token, todayDate, yesterdayDate]
  );

  useEffect(() => {
    refreshStatuses({ showReminder: true });
  }, [refreshStatuses]);

  useEffect(() => {
    const checkForNewBusinessDay = () => {
      const currentDate = dayjs().format("YYYY-MM-DD");
      setTodayDate((previousDate) =>
        previousDate === currentDate ? previousDate : currentDate
      );
    };
    const intervalId = window.setInterval(checkForNewBusinessDay, 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  const overdueEndingYesterday =
    yesterdayStatus?.beginningInventory?.status === "completed" &&
    yesterdayStatus?.endingInventory?.status !== "completed";

  const claimStockAlertDisplay = useCallback(() => {
    if (stockAlertShownRef.current) return false;
    stockAlertShownRef.current = true;
    return true;
  }, []);

  const value = useMemo(
    () => ({
      dailyTabStatus: getDailyTabStatus(todayStatus),
      claimStockAlertDisplay,
      loading,
      overdueEndingYesterday,
      refreshStatuses,
      todayDate,
      todayStatus,
      yesterdayDate,
      yesterdayStatus,
    }),
    [
      claimStockAlertDisplay,
      loading,
      overdueEndingYesterday,
      refreshStatuses,
      todayDate,
      todayStatus,
      yesterdayDate,
      yesterdayStatus,
    ]
  );

  const startInventory = () => {
    const selectedMode = reminderMode || "beginning";
    setReminderMode(null);
    navigate(
      `/inventory/daily-stock?mode=${selectedMode}&businessDate=${todayDate}`
    );
  };

  return (
    <InventoryModuleStatusContext.Provider value={value}>
      <Outlet />
      <InventoryReminderDialog
        open={Boolean(reminderMode)}
        mode={reminderMode || "beginning"}
        onStart={startInventory}
        onLater={() => setReminderMode(null)}
      />
    </InventoryModuleStatusContext.Provider>
  );
};

export const useInventoryModuleStatus = () => {
  const context = useContext(InventoryModuleStatusContext);
  if (!context) {
    throw new Error(
      "useInventoryModuleStatus must be used inside InventoryModuleStatusProvider."
    );
  }
  return context;
};
